import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import LocationPickerModal from '../components/LocationPickerModal';
import LocationPinIcon from '../components/LocationPinIcon';
import TrashIcon from '../components/TrashIcon';
import { readSavedDeliveryLocation } from '../lib/deliveryLocation';
import { DEFAULT_PREPARATION_DAYS, formatBusinessDays } from '../lib/preparationTime';

const safeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
};

const itemQuantity = (item) => {
  const quantity = Math.floor(safeNumber(item.quantity));
  return quantity > 0 ? quantity : 1;
};
const itemVariantId = (item) => item.variant_id ?? item.variantId ?? null;
const itemMatchesOrigin = (item, origin) => {
  const productId = String(item.product_id || item.id);
  const variantId = String(itemVariantId(item) ?? '');
  return origin?.items?.some((originItem) => (
    String(originItem.productId || originItem.product_id) === productId
    && String(originItem.variantId ?? originItem.variant_id ?? '') === variantId
  ));
};

const getItemDeliveryDetails = (item, quote) => {
  return quote?.originBreakdown?.find((origin) => itemMatchesOrigin(item, origin)) || null;
};

const readJsonSafely = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

export default function CartPage() {
  const { cartItems, cartLoading, updateQuantity, removeFromCart, clearCart } = useContext(CartContext);
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [deliveryLocation, setDeliveryLocation] = useState(() => readSavedDeliveryLocation());
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const [shippingQuote, setShippingQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);

  const subtotal = cartItems.reduce(
    (sum, item) => sum + Number(item.final_price || item.price_mrp || 0) * (item.quantity || 1),
    0
  );
  const quoteItems = useMemo(() => cartItems.map((item) => ({
    productId: item.product_id || item.id,
    variantId: itemVariantId(item),
    quantity: itemQuantity(item),
  })), [cartItems]);
  const shippingInfoReady = Boolean(
    deliveryLocation?.city?.trim() &&
    deliveryLocation?.state?.trim() &&
    /^\d{6}$/.test(deliveryLocation?.zip?.trim() || '') &&
    deliveryLocation?.country?.trim().toLowerCase() === 'india'
  );
  const shippingCost = safeNumber(shippingQuote?.shippingCost);
  const total = subtotal + shippingCost;
  const cartSourceGroups = useMemo(() => {
    const indexedItems = cartItems.map((item, index) => ({ item, index }));
    if (!shippingQuote?.originBreakdown?.length) {
      return [{ key: 'cart', origin: null, items: indexedItems }];
    }

    const claimedIndexes = new Set();
    const groups = shippingQuote.originBreakdown
      .map((origin, groupIndex) => {
        const items = indexedItems.filter(({ item, index }) => {
          if (claimedIndexes.has(index) || !itemMatchesOrigin(item, origin)) return false;
          claimedIndexes.add(index);
          return true;
        });
        return items.length > 0 ? { key: origin.originCode || `source-${groupIndex}`, origin, items } : null;
      })
      .filter(Boolean);
    const unmatchedItems = indexedItems.filter(({ index }) => !claimedIndexes.has(index));
    if (unmatchedItems.length > 0) groups.push({ key: 'unmatched', origin: null, items: unmatchedItems });
    return groups;
  }, [cartItems, shippingQuote]);

  useEffect(() => {
    const applySavedLocation = (event) => setDeliveryLocation(event.detail || readSavedDeliveryLocation());
    window.addEventListener('hunar:delivery-location-updated', applySavedLocation);
    return () => window.removeEventListener('hunar:delivery-location-updated', applySavedLocation);
  }, []);

  useEffect(() => {
    if (!shippingInfoReady || quoteItems.length === 0) {
      setShippingQuote(null);
      setQuoteError(null);
      setQuoteLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setQuoteLoading(true);
      setQuoteError(null);
      try {
        const response = await fetch('/api/shipping/quote', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            shippingInfo: {
              city: deliveryLocation.city,
              state: deliveryLocation.state,
              zip: deliveryLocation.zip,
              country: deliveryLocation.country,
            },
            items: quoteItems,
            paymentMethod: 'prepaid',
            fulfillmentMethod: 'delivery',
          }),
          signal: controller.signal,
        });
        const data = await readJsonSafely(response);
        if (!response.ok) throw new Error(data.msg || 'Delivery charges are unavailable right now.');
        setShippingQuote(data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          setShippingQuote(null);
          setQuoteError(error.message || 'Delivery charges are unavailable right now.');
        }
      } finally {
        if (!controller.signal.aborted) setQuoteLoading(false);
      }
    }, 250);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [deliveryLocation, quoteItems, shippingInfoReady]);

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center gap-3 mb-4">
        <h1 className="brand-font mb-0">Shopping Cart</h1>
        {!cartLoading && cartItems.length > 0 && (
          <button type="button" className="btn btn-sm btn-outline-custom flex-shrink-0" onClick={() => setLocationPickerOpen(true)}>
            {shippingInfoReady ? 'Change location' : 'Set location'}
          </button>
        )}
      </div>

      {cartLoading ? (
        <div className="text-center py-5 border rounded bg-card" role="status" aria-live="polite">
          Loading your cart…
        </div>
      ) : cartItems.length === 0 ? (
        <div className="text-center py-5 border rounded bg-card">
          <div className="fs-1 mb-2">🛒</div>
          <h5>Your cart is currently empty</h5>
          <p className="text-muted small">Explore our handmade creations and add something lovely to your cart!</p>
          <Link to="/shop" className="btn btn-primary-custom rounded-pill mt-2">
            Explore Collection →
          </Link>
        </div>
      ) : (
        <div className="row g-4">
          {/* Cart Items List */}
          <div className="col-12 col-lg-8">
            <div className="border rounded bg-card p-3 shadow-sm">
              <h2 className="cart-products-heading">Products</h2>
              {cartSourceGroups.map((group) => (
                <div key={group.key} className="cart-source-group">
                  {group.items.map(({ item, index: idx }) => (
                    <div key={`${item.product_id || item.id}-${itemVariantId(item) ?? 'base'}-${idx}`} className="cart-item-row d-flex align-items-center gap-3 py-3 border-bottom last-border-0">
                  <img
                    src={
                      item.cover_image ||
                      item.images?.[0] ||
                      'https://images.unsplash.com/photo-1584992236310-6edddc08acff?auto=format&fit=crop&w=800&q=80'
                    }
                    alt={item.title}
                    className="rounded"
                    style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                  />

                  <div className="flex-grow-1">
                    <h6 className="font-weight-bold mb-1">{item.title}</h6>
                    {item.variant_name && <div className="small text-muted mb-1">Size: {item.variant_name}</div>}
                    {(() => {
                      const delivery = getItemDeliveryDetails(item, shippingQuote);
                      const preparationDays = delivery?.preparationDays ?? item.preparation_days ?? DEFAULT_PREPARATION_DAYS;
                      return (
                        <div className="cart-item-delivery-line">
                          <span>Preparation: {formatBusinessDays(preparationDays)}</span>
                          <span>Delivery after dispatch: {delivery?.estimatedDays || (quoteError ? 'Unavailable' : shippingInfoReady ? 'Calculating…' : 'Set location to estimate')}</span>
                          {delivery?.originCity && (
                            <span
                              className="cart-item-origin-tooltip"
                              tabIndex="0"
                              aria-label={`Ships from ${delivery.originCity}, ${delivery.originState || ''}`.replace(/, $/, '')}
                            >
                              <LocationPinIcon className="cart-item-origin-icon" />
                              <span className="cart-item-origin-tooltip__bubble" role="tooltip">
                                From {delivery.originCity}{delivery.originState ? `, ${delivery.originState}` : ''}
                              </span>
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    <span className="text-primary font-weight-bold">
                      ₹{item.final_price || item.price_mrp}
                    </span>
                  </div>

                  {/* Quantity Controls */}
                  <div className="input-group input-group-sm cart-quantity-controls">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => updateQuantity(item.product_id || item.id, -1, itemVariantId(item))}
                    >
                      -
                    </button>
                    <span className="form-control text-center bg-transparent border-secondary">
                      {item.quantity || 1}
                    </span>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => updateQuantity(item.product_id || item.id, 1, itemVariantId(item))}
                    >
                      +
                    </button>
                  </div>

                  <div className="text-end cart-line-total" style={{ minWidth: '80px' }}>
                    <span className="font-weight-bold">
                      ₹{((item.final_price || item.price_mrp) * (item.quantity || 1)).toFixed(2)}
                    </span>
                  </div>

                  <button
                    className="btn btn-sm btn-outline-danger border-0 cart-remove"
                    onClick={() => removeFromCart(item.product_id || item.id, itemVariantId(item))}
                    title="Remove item"
                  >
                    <TrashIcon />
                  </button>
                    </div>
                  ))}
                  {group.origin && group.origin.shippingCost !== undefined && group.origin.shippingCost !== null && (
                    <div className="cart-source-group__charge">
                      <span>{group.origin.originLabel || 'Source'} delivery charges</span>
                      <strong>{Number(group.origin.shippingCost) === 0 ? 'FREE' : `₹${Number(group.origin.shippingCost).toFixed(2)}`}</strong>
                    </div>
                  )}
                </div>
              ))}

              {quoteError && <div className="cart-delivery-quote cart-delivery-quote--error mt-3" role="alert">{quoteError}</div>}
              {shippingQuote?.partialPickup?.available && (
                <div className="cart-partial-pickup-note mt-3" role="status">
                  <strong>Some items may be collected nearby.</strong>
                  <span>Collect separately: {shippingQuote.partialPickup.collectableItems.map((item) => item.title || item.name || 'Handmade item').join(', ')}.</span>
                  <span>Delivered: {shippingQuote.partialPickup.deliveryItems.map((item) => item.title || item.name || 'Handmade item').join(', ')}.</span>
                </div>
              )}

              <div className="d-flex justify-content-start pt-3">
                <button className="btn btn-sm btn-outline-secondary" onClick={clearCart}>
                  Clear Cart
                </button>
              </div>
            </div>
          </div>

          {/* Order Summary Side Card */}
          <div className="col-12 col-lg-4">
            <div className="p-4 border rounded bg-card shadow-sm sticky-top" style={{ top: '90px' }}>
              <h5 className="brand-font mb-3">Order Summary</h5>

              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Subtotal:</span>
                <span className="font-weight-bold">₹{subtotal.toFixed(2)}</span>
              </div>

              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Shipping:</span>
                <span className="font-weight-bold">
                  {quoteLoading ? 'Calculating…' : shippingQuote ? shippingCost === 0 ? <span className="text-success">FREE</span> : `₹${shippingCost.toFixed(2)}` : 'Set location'}
                </span>
              </div>

              <hr />

              <div className="d-flex justify-content-between fs-5 font-weight-bold mb-4">
                <span>Total:</span>
                <span className="text-primary">
                  ₹{total.toFixed(2)}
                </span>
              </div>

              {!shippingQuote && !quoteLoading && (
                <p className="text-muted small mb-3">Total updates after you set a delivery location.</p>
              )}

              <button
                className="btn btn-lg btn-primary-custom w-100 rounded-pill font-weight-bold py-3 shadow-sm"
                onClick={() => navigate('/checkout')}
              >
                Proceed to Checkout →
              </button>
            </div>
          </div>
        </div>
      )}

      <LocationPickerModal
        open={locationPickerOpen}
        onClose={() => setLocationPickerOpen(false)}
        savedLocation={deliveryLocation}
        onSaved={setDeliveryLocation}
        token={token}
      />
    </div>
  );
}
